import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Tooltip,
  LinearProgress,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Refresh as RefreshIcon,
  VerifiedUser as VerifyIcon,
  People as UsersIcon,
  InsertChartOutlined as ChartIcon,
  TrendingUp as TrendIcon,
  WarningAmber as WarningIcon,
  DoneAll as DoneIcon,
  InfoOutlined as InfoIcon,
  Business as CompaniesIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import apiService from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import analyticsDashboardService from '../../services/analyticsDashboard';
import {
  InvoiceWithRelations,
  InvoiceStatus,
  User,
  Company,
  Transaction,
  DashboardStats,
  DailyMetric,
} from '../../types/models';

const KPI_PALETTE = ['#6C63FF', '#FF6584', '#2EC4B6', '#F3A712', '#3F8EFC'];

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [pendingInvoices, setPendingInvoices] = useState<InvoiceWithRelations[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [dailyMetrics, setDailyMetrics] = useState<DailyMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'7' | '30' | '90'>('30');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [invoicesData, usersData, companiesData, transactionsData, statsData] = await Promise.all([
          apiService.get<InvoiceWithRelations[]>(`/invoices?status=${InvoiceStatus.PENDING}`),
          apiService.get<User[]>('/users'),
          apiService.get<Company[]>('/companies'),
          apiService.get<Transaction[]>('/transactions'),
          analyticsDashboardService.getDashboardStats(Number(dateRange)),
        ]);

        setPendingInvoices(invoicesData);
        setUsers(usersData);
        setCompanies(companiesData);
        setTransactions(transactionsData);
        setDashboardStats(statsData);
        setDailyMetrics(statsData.daily_metrics || []);
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail
          ? typeof err.response.data.detail === 'string'
            ? err.response.data.detail
            : JSON.stringify(err.response.data.detail)
          : 'Error al cargar los datos del panel';

        setError(errorMessage);
        console.error('Dashboard data fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRange]);

  const getStatusColor = (status: InvoiceStatus) => {
    switch (status) {
      case InvoiceStatus.PENDING:
        return 'warning';
      case InvoiceStatus.VERIFIED:
        return 'success';
      case InvoiceStatus.SOLD:
        return 'info';
      case InvoiceStatus.EXPIRED:
        return 'error';
      case InvoiceStatus.REJECTED:
        return 'error';
      case InvoiceStatus.DELISTED:
        return 'default';
      default:
        return 'default';
    }
  };


  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'Sin fecha';
    const date = new Date(dateString + (dateString.includes('Z') || dateString.includes('+') ? '' : 'Z'));
    if (isNaN(date.getTime())) return 'Fecha inválida';
    return date.toLocaleDateString('es-PY');
  };

  const kpiCards = useMemo(() => {
    if (!dashboardStats) return [];
    const { totals } = dashboardStats;
    const verifiedRatio = totals.invoices ? (totals.verified_invoices / totals.invoices) * 100 : 0;
    const conversion = totals.bids ? (totals.accepted_bids / totals.bids) * 100 : 0;
    return [
      {
        title: 'Facturas Publicadas',
        value: totals.invoices,
        icon: <VerifyIcon color="inherit" />,
        helper: `${totals.verified_invoices} verificadas (${verifiedRatio.toFixed(1)}%)`,
      },
      {
        title: 'Usuarios Totales',
        value: totals.users,
        icon: <UsersIcon color="inherit" />,
        helper: `${totals.sellers} vendedores · ${totals.buyers} compradores`,
      },
      {
        title: 'Ofertas Recibidas',
        value: totals.bids,
        icon: <TrendIcon color="inherit" />,
        helper: `${conversion.toFixed(1)}% aceptadas`,
      },
      {
        title: 'Eventos 7 días',
        value: dashboardStats.recent_activity.events_last_7_days,
        icon: <ChartIcon color="inherit" />,
        helper: 'Interacciones registradas',
      },
    ];
  }, [dashboardStats]);

  const supplyDemandData = useMemo(() => {
    if (!dailyMetrics.length) return [];
    return dailyMetrics.map((metric) => ({
      date: new Date(metric.date).toLocaleDateString('es-PY', { month: 'short', day: 'numeric' }),
      supply: metric.invoices_uploaded,
      verified: metric.invoices_verified,
      demand: metric.bids_placed,
      accepted: metric.bids_accepted,
    }));
  }, [dailyMetrics]);

  const funnelData = useMemo(() => {
    if (!dashboardStats) return [];
    return [
      { label: 'Registrados', value: dashboardStats.totals.users },
      { label: 'Verificados', value: dashboardStats.totals.buyers + dashboardStats.totals.sellers },
      { label: 'Suben Facturas', value: dashboardStats.totals.sellers },
      { label: 'Con ofertas', value: dashboardStats.totals.bids },
      { label: 'Financiados', value: dashboardStats.totals.sold_invoices },
    ];
  }, [dashboardStats]);

  const alerts = useMemo(() => {
    const items: { severity: 'warning' | 'info' | 'success'; message: string }[] = [];
    if (pendingInvoices.length > 0) {
      items.push({
        severity: 'warning',
        message: `${pendingInvoices.length} facturas esperan revisión.`,
      });
    }
    if (dashboardStats && dashboardStats.totals.accepted_bids === 0 && dashboardStats.totals.bids > 0) {
      items.push({ severity: 'info', message: 'Ofertas sin aceptar en el periodo seleccionado.' });
    }
    if (!items.length) {
      items.push({ severity: 'success', message: 'Sin alertas operativas 👍' });
    }
    return items;
  }, [pendingInvoices.length, dashboardStats]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700}>
            Panel de Analítica
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Salud del marketplace y acciones prioritarias
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-end">
          <ToggleButtonGroup
            size="small"
            value={dateRange}
            exclusive
            onChange={(_, value) => value && setDateRange(value)}
            color="primary"
          >
            <ToggleButton value="7">7 días</ToggleButton>
            <ToggleButton value="30">30 días</ToggleButton>
            <ToggleButton value="90">90 días</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={<VerifyIcon />}
            onClick={() => navigate('/admin/invoices')}
          >
            Revisar Facturas
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* KPI Cards */}
      <Grid container spacing={3} mb={4}>
        {kpiCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Card sx={{ borderTop: `4px solid ${KPI_PALETTE[index % KPI_PALETTE.length]}` }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="text.secondary">
                    {card.title}
                  </Typography>
                  <Box color={KPI_PALETTE[index % KPI_PALETTE.length]}>{card.icon}</Box>
                </Stack>
                <Typography variant="h4" fontWeight={600} mt={1}>
                  {card.value.toLocaleString('es-PY')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.helper}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Oferta vs Demanda</Typography>
                <Tooltip title="Facturas vs Ofertas recibidas en el periodo seleccionado">
                  <TrendIcon color="action" />
                </Tooltip>
              </Stack>
              <Box height={300}>
                {supplyDemandData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={supplyDemandData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip />
                      <Line type="monotone" dataKey="supply" name="Facturas" stroke="#6C63FF" strokeWidth={2} />
                      <Line type="monotone" dataKey="demand" name="Ofertas" stroke="#2EC4B6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <Typography color="text.secondary">Sin datos suficientes aún.</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Distribución del flujo</Typography>
                <ChartIcon color="action" />
              </Stack>
              <Box height={280}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={funnelData} dataKey="value" nameKey="label" outerRadius={110}>
                      {funnelData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={KPI_PALETTE[index % KPI_PALETTE.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Embudo de usuarios</Typography>
                <Tooltip title="Suma aproximada por etapa. Clic para filtrar en futuro release.">
                  <InfoIcon color="action" />
                </Tooltip>
              </Stack>
              <Stack spacing={2}>
                {funnelData.map((step, index) => (
                  <Box key={step.label}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography fontWeight={600}>{step.label}</Typography>
                      <Typography color="text.secondary">{step.value.toLocaleString('es-PY')}</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={funnelData[0].value ? (step.value / funnelData[0].value) * 100 : 0}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: KPI_PALETTE[index % KPI_PALETTE.length],
                        },
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Alertas operativas</Typography>
                <WarningIcon color="warning" />
              </Stack>
              <Stack spacing={1.5}>
                {alerts.map((alert, index) => (
                  <Alert key={index} severity={alert.severity} icon={<DoneIcon fontSize="small" />}>
                    {alert.message}
                  </Alert>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Facturas Pendientes</Typography>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => window.location.reload()}>
            Actualizar
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Factura #</TableCell>
                <TableCell>Vendedor</TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>Monto Neto</TableCell>
                <TableCell>Fecha de Envío</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" py={2}>
                      No hay facturas pendientes para verificar en este momento.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pendingInvoices
                  .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                  .slice(0, 5)
                  .map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>{invoice.invoice_number}</TableCell>
                      <TableCell>{invoice.seller ? invoice.seller.full_name : 'Vendedor Desconocido'}</TableCell>
                      <TableCell>{invoice.debtor ? invoice.debtor.name : 'Deudor Desconocido'}</TableCell>
                      <TableCell>{formatCurrency(invoice.amount, invoice.currency)}</TableCell>
                      <TableCell>{formatDate(invoice.created_at)}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => navigate('/admin/invoices', { state: { editInvoice: invoice } })}
                        >
                          Verificar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button onClick={() => navigate('/admin/invoices')}>Ver Todas las Facturas Pendientes</Button>
        </Box>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">Usuarios Recientes</Typography>
            <Button 
              variant="outlined" 
              startIcon={<UsersIcon />}
              onClick={() => navigate('/users')}
              size="small"
            >
              Gestionar Usuarios
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Correo Electrónico</TableCell>
                  <TableCell>Rol</TableCell>
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body1" py={2}>
                        No se encontraron usuarios.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                    .slice(0, 5)
                    .map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.full_name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          {user.is_admin ? 'Administrador' : user.is_buyer ? (user.is_seller ? 'Comprador/Vendedor' : 'Comprador') : 'Vendedor'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={user.is_active ? 'Activo' : 'Inactivo'} 
                            color={user.is_active ? 'success' : 'error'} 
                            size="small" 
                          />
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">Empresas Recientes</Typography>
            <Button 
              variant="outlined" 
              startIcon={<CompaniesIcon />}
              onClick={() => navigate('/companies')}
              size="small"
            >
              Gestionar Empresas
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>RUC</TableCell>
                  <TableCell>Fecha de Registro</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body1" py={2}>
                        No se encontraron empresas.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  companies
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                    .slice(0, 5)
                    .map((company) => (
                      <TableRow key={company.id}>
                        <TableCell>{company.name}</TableCell>
                        <TableCell>{company.tax_id}</TableCell>
                        <TableCell>{formatDate(company.created_at)}</TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
